import { buildClasses } from "../utils/buildClasses";
import bread_logo from "../images/bread_logo_600.png";
import { RyeButton } from "../rye/RyeButton";
import { RYE_PANEL_SURFACE } from "../rye/RyeScreenWrapper";
import { RyeAnimatedHeight } from "../rye/RyeAnimatedHeight";
import React, { ReactElement } from "react";
import { Link } from "react-router-dom";

interface Footer {
  promptText: string;
  actionText: string;
  onActionClick: () => void;
}

export function AuthScreenBase({
  title,
  content,
  footer,
  backButtonAction = null,
  contentGap = "lg",
  logoLinksHome = false,
}: {
  title: string;
  content: React.ReactNode;
  footer: Footer;
  backButtonAction?: (() => void) | null;
  contentGap?: "sm" | "lg";
  /** Clicking the logo goes to the website's home page. */
  logoLinksHome?: boolean;
}): JSX.Element {
  return (
    <div className={buildClasses("h-screen")}>
      <div
        className={buildClasses(
          "flex",
          "flex-col",
          "items-center",
          "h-full",
          "bg-base",
          // So the card's shadow, drawn beneath it, paints over this background.
          "isolate",
        )}
      >
        <div className={buildClasses("w-full", "h-[13%]")} />
        <div className={buildClasses("p-10")}>
          {logoLinksHome ? (
            <Link to="/" aria-label="Go to the Bread home page">
              <BreadLogo />
            </Link>
          ) : (
            <BreadLogo />
          )}
        </div>
        {/* Inset from narrow windows' edges, which the card shrinks to fit. */}
        <div className={buildClasses("max-w-full", "px-4", "pb-52")}>
          <AuthContentCard
            title={title}
            content={content}
            contentGap={contentGap}
            backButtonAction={backButtonAction}
          />
          <AuthFooter footer={footer} />
        </div>
      </div>
    </div>
  );
}

function BreadLogo(): JSX.Element {
  return (
    <img
      src={bread_logo}
      className={buildClasses("w-16")}
      alt="Bread logo"
    ></img>
  );
}

function AuthContentCard(props: {
  title: string;
  content: React.ReactNode;
  contentGap: "sm" | "lg";
  backButtonAction: (() => void) | null;
}): JSX.Element {
  return (
    // The app's panel: its surface, warm outline, and shadow. It grows and
    // shrinks smoothly as its contents change, such as on choosing email.
    <div
      className={buildClasses(
        "relative",
        RYE_PANEL_SURFACE,
        "w-[26rem]",
        "max-w-full",
      )}
    >
      <RyeAnimatedHeight>
        <div
          className={buildClasses(
            "flex",
            "flex-col",
            "items-center",
            "justify-center",
            "gap-10",
            "pb-10",
          )}
        >
          <CardTitle
            title={props.title}
            backButtonAction={props.backButtonAction}
          />
          <CardContent content={props.content} contentGap={props.contentGap} />
        </div>
      </RyeAnimatedHeight>
    </div>
  );
}

function CardTitle(props: {
  title: string;
  backButtonAction: (() => void) | null;
}): JSX.Element {
  return (
    <React.Fragment>
      <div className={buildClasses("p-10", "pb-0", "w-full")}>
        <div
          className={buildClasses(
            "relative",
            "text-lg",
            "font-medium",
            "w-full",
            "flex",
            "justify-center",
          )}
        >
          <CardBackButton action={props.backButtonAction} />
          {props.title}
        </div>
      </div>
    </React.Fragment>
  );
}

function CardBackButton(props: {
  action: (() => void) | null;
}): ReactElement | null {
  if (props.action === null) {
    return null; // don't show the back button at all
  }
  return (
    <div className={buildClasses("absolute", "top-0", "left-0")}>
      <RyeButton
        size="sm"
        icon="arrow_back"
        variant="transparent"
        vibe="subdued"
        onClick={props.action}
      />
    </div>
  );
}

function CardContent(props: {
  content: React.ReactNode;
  contentGap: "sm" | "lg";
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        {
          switch: props.contentGap,
          cases: new Map([
            ["sm", buildClasses("gap-5")],
            ["lg", buildClasses("gap-10")],
          ]),
        },
        "w-full",
        "flex",
        "flex-col",
        "px-10",
      )}
    >
      {props.content}
    </div>
  );
}

/** The way to the other screen, below the card on the page. */
function AuthFooter(props: { footer: Footer }): JSX.Element {
  return (
    <div className={buildClasses("w-full", "pt-6")}>
      <div
        className={buildClasses(
          "flex",
          "justify-center",
          "w-full",
          "text-[0.8rem]",
          "text-on-surface-500",
        )}
      >
        {props.footer.promptText}
        <span
          className={buildClasses(
            "text-[0.8rem]",
            "text-purple",
            "ml-1",
            "cursor-pointer",
            "hover:text-purple-800",
          )}
          onClick={props.footer.onActionClick}
        >
          {props.footer.actionText}
        </span>
      </div>
    </div>
  );
}
